/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Button,
    Typography,
    LinearProgress,
    Snackbar,
    Alert,
} from "@mui/material";
import JSZip from "jszip"; // Import JSZip for zipping files
import API_ENDPOINTS from "../../../apis/endpoints";
import useAuth from "../../../context";
import AiAvatarProcess from "./AiAvatarProcess";

function AiAvatarUploadProcess({ onNext, onBack, data }) {
    const auth = useAuth();
    const [uploadedCount, setUploadedCount] = useState(0);
    const [totalFiles, setTotalFiles] = useState(0);
    const [uploading, setUploading] = useState(true);
    const [fileIds, setFileIds] = useState([]);
    const [showCloningProcess, setShowCloningProcess] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const storedDeviceId = localStorage.getItem("device-id");

    const zipAndUploadFiles = async (folderId, files) => {
        // Zip the files first
        const zipBlob = await zipFiles(files);

        // Now upload the zip file and get file IDs
        const uploadedFileIds = await uploadZipFile(zipBlob, folderId);

        // Set uploaded count and file IDs after upload is complete
        setUploadedCount(files.length);
        setUploading(false);
    };

    const zipFiles = async (files) => {
        const zip = new JSZip();
        files.forEach((file) => {
            zip.file(file.name, file);
        });
        return await zip.generateAsync({ type: "blob" });
    };

    const uploadZipFile = async (zipBlob, folderId) => {
        const formData = new FormData();
        formData.append("folder_id", folderId);
        formData.append("folder_type", "ai-models/avatar");
        formData.append("file_name", "images.zip");
        formData.append("file_type", "application/zip");
        formData.append("file_size", zipBlob.size.toString());
        formData.append("public", true); // Set public to true

        try {
            const uploadResponse = await fetch(API_ENDPOINTS.STORAGE, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${auth.accessToken}`,
                    "x-cc-device-id": storedDeviceId,
                },
                body: formData,
            });

            if (!uploadResponse.ok) {
                throw new Error("Failed to upload zip file");
            }

            // Get the response data
            const uploadData = await uploadResponse.json();

            const putResponse = await fetch(uploadData.data.upload_url, {
                method: "PATCH",
                body: zipBlob,
                headers: {
                    "Content-Type": zipBlob.type,
                },
            });

            if (!putResponse.ok) {
                throw new Error(`Failed to upload file: ${zipBlob.name}`);
            }

            setFileIds(uploadData.data.file_id);

            return uploadData.data.file_id; // Return file ID for further processing
        } catch (error) {
            console.error("Error uploading zip file:", error);
            setSnackbarMessage(error.message); // Set snackbar message
            setSnackbarOpen(true); // Open snackbar on error
            return []; // Return empty array on error
        }
    };

    useEffect(() => {
        if (data && data.files) {
            setTotalFiles(data.files.length);

            // Start zipping and uploading files
            zipAndUploadFiles(data.folderId, data.files);
        }
    }, [data]);

    const handleContinue = async () => {
        try {
            const response = await fetch(
                `${API_ENDPOINTS.AI_MODEL_AVATAR}${data.folderId}/`,
                {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": storedDeviceId,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ attachment: fileIds }),
                }
            );

            if (!response.ok) {
                throw new Error("Failed to update Avatar model");
            }

            setShowCloningProcess(true);
        } catch (error) {
            console.error("Error updating Avatar model:", error);
            setSnackbarMessage(error.message); // Set snackbar message
            setSnackbarOpen(true); // Open snackbar on error
        }
    };

    if (showCloningProcess) {
        return <AiAvatarProcess onBack={() => setShowCloningProcess(false)} />;
    }

    return (
        <div style={{ padding: "20px" }}>
            <Typography variant="highlightSemibold">
                Uploading images ({uploadedCount}/{totalFiles})
            </Typography>

            <LinearProgress
                variant="determinate"
                value={(uploadedCount / totalFiles) * 100}
            />

            <Typography variant="body2">
                {uploadedCount} out of {totalFiles} files uploaded
            </Typography>

            <Button
                variant="contained"
                onClick={handleContinue}
                disabled={uploading}
            >
                Continue
            </Button>

            {/* Snackbar for displaying errors */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity="error"
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default AiAvatarUploadProcess;
